import React from 'react';
import { HiOutlineX } from 'react-icons/hi';
import { getImageUrl, getImageCacheKey } from '../../../lib/helpers';

function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('es-ES');
}

function Row({ label, value, valueClass = '' }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className={`text-sm font-medium text-gray-900 text-right break-words ${valueClass}`}>{value}</span>
    </div>
  );
}

/**
 * Panel lateral "Detalle socio" — específico de la pantalla Socios (Figma).
 * No usa el modal compartido (CustomModal) para poder ser fiel al diseño.
 */
export default function SocioDetailPanel({ active, client, onClose, onManageBalance, onViewFullProfile }) {
  if (!active || !client) return null;
  const p = client.profile || {};
  const name = p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : (client.email || 'Socio');
  const img = getImageUrl(p.documents_path || p.image_path, { cacheKey: getImageCacheKey('client', client.id) });
  const exp = p.expiration_date ? new Date(p.expiration_date) : null;
  const expired = exp ? exp < new Date(new Date().toDateString()) : false;
  const estado = expired ? 'Caducado' : 'Activo';
  const bal = Number(p.credit_balance) || 0;
  const tipo = (() => {
    const l = p.consumption_type?.ludenos, t = p.consumption_type?.terap;
    if (l && t) return 'Lúdico / Terapéutico';
    if (l) return 'Lúdico';
    if (t) return 'Terapéutico';
    return '—';
  })();
  const direccion = [p.address, p.city].filter(Boolean).join(', ') || '—';

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Barra superior */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-900">Detalle socio</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Cerrar">
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Cabecera verde */}
          <div className="rounded-2xl bg-[linear-gradient(135deg,_#16a34a,_#4ade80_70%,_#bbf7d0)] p-5 text-center text-white shadow-sm">
            {img ? (
              <img
                src={img}
                alt=""
                className="mx-auto w-20 h-20 rounded-full object-cover border-2 border-white/60"
                onError={(e) => { e.target.style.display = 'none'; const s = e.target.nextElementSibling; if (s) { s.classList.remove('hidden'); s.classList.add('flex'); } }}
              />
            ) : null}
            <div className={`mx-auto w-20 h-20 rounded-full bg-white/25 border border-white/40 items-center justify-center text-2xl font-bold ${img ? 'hidden' : 'flex'}`}>
              {name.charAt(0).toUpperCase()}
            </div>
            <div className="mt-3 text-lg font-bold">{name}</div>
            <div className="mt-1 flex items-center justify-center gap-2 text-sm">
              <span>Nº {client.membership_id ?? '—'}</span>
              <span className="rounded-full bg-white/25 px-2.5 py-0.5 text-xs font-medium">{estado}</span>
            </div>
          </div>

          {/* Saldo disponible */}
          <div className="rounded-2xl border border-gray-200 px-5 py-4 text-center">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">Saldo disponible</div>
            <div className="mt-1 text-3xl font-bold text-gray-900">
              {bal <= 0 ? <span className="text-red-600">€0,00</span> : `€${bal.toFixed(2)}`}
            </div>
          </div>

          {/* Contacto */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Contacto</div>
            <Row label="Teléfono" value={p.phone || '—'} />
            <Row label="Email" value={client.email || p.email || '—'} />
            <Row label="Dirección" value={direccion} />
          </div>

          {/* Membresía */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Membresía</div>
            <Row label="Alta" value={fmtDate(p.enrollment_date)} />
            <Row label="Caducidad" value={fmtDate(p.expiration_date)} valueClass={expired ? 'text-red-600' : ''} />
            <Row label="Tipo de consumo" value={tipo} />
            <Row label="Cuota" value={p.fee || '—'} />
          </div>
        </div>

        {/* Acciones */}
        <div className="border-t border-gray-100 p-4 flex gap-2">
          <button
            onClick={onManageBalance}
            className="flex-1 h-11 rounded-lg border border-gray-300 text-gray-800 font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            Gestionar saldo
          </button>
          <button
            onClick={onViewFullProfile}
            className="flex-1 h-11 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors"
          >
            Ver perfil completo
          </button>
        </div>
      </div>
    </div>
  );
}
